import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Trophy, Smartphone, Globe, Users, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter 80 — admin leaderboard of top ambassadors ranked by click
 * volume. Loaded on mount, refreshed on demand via the header button.
 */
export default function AdminReferralLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetch = () => {
    setLoading(true);
    api.get("/admin/referral-leaderboard", { params: { limit: 20 } })
      .then(({ data }) => setRows(data.leaderboard || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      // Fetch as blob so the Authorization header is attached and the
      // CSV filename from Content-Disposition is used by the browser.
      const resp = await api.get("/admin/referral-leaderboard.csv", {
        params: { limit: 500 },
        responseType: "blob",
      });
      const match = /filename="?([^";]+)"?/.exec(resp.headers["content-disposition"] || "");
      const fname = match ? match[1] : "ambassadeurs.csv";
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Classement exporté");
    } catch {
      toast.error("Export impossible");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="admin-referral-leaderboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FACC15]" />
          <h3 className="text-base font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            Top Ambassadeurs
          </h3>
        </div>
        <button
          onClick={fetch}
          className="h-8 px-3 rounded-lg bg-[#0A2540] text-white text-xs font-semibold hover:bg-[#081b2e]"
          data-testid="leaderboard-refresh"
        >
          Actualiser
        </button>
        <button
          onClick={downloadCsv}
          disabled={exporting}
          className="h-8 px-3 rounded-lg bg-[#FACC15] text-[#0A2540] text-xs font-bold hover:bg-[#EAB308] inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="leaderboard-csv-export"
        >
          <Download className="w-3.5 h-3.5" /> {exporting ? "..." : "CSV"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#64748B] py-6 text-center">Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#64748B] py-6 text-center">Aucun partage tracké pour l'instant.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B] border-b border-[#CBD5E1]">
                <th className="py-2 px-2">#</th>
                <th className="py-2 px-2">Ambassadeur</th>
                <th className="py-2 px-2">Code</th>
                <th className="py-2 px-2 text-right">Clics</th>
                <th className="py-2 px-2 text-right"><Smartphone className="inline w-3 h-3" /> Android</th>
                <th className="py-2 px-2 text-right"><Smartphone className="inline w-3 h-3" /> iOS</th>
                <th className="py-2 px-2 text-right"><Globe className="inline w-3 h-3" /> Web</th>
                <th className="py-2 px-2 text-right"><Users className="inline w-3 h-3" /> Signups</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.referral_code} className="border-b border-[#F1F5F9]" data-testid={`leaderboard-row-${i}`}>
                  <td className="py-2 px-2 font-bold text-[#0A2540]">{i + 1}</td>
                  <td className="py-2 px-2">
                    <p className="font-semibold text-[#0A2540]">{r.name}</p>
                    <p className="text-[11px] text-[#64748B]">{r.phone || "—"} · {r.role}</p>
                  </td>
                  <td className="py-2 px-2"><code className="text-[11px] bg-[#F1F5F9] px-1.5 py-0.5 rounded">{r.referral_code}</code></td>
                  <td className="py-2 px-2 text-right font-bold text-[#0A2540]">{r.clicks}</td>
                  <td className="py-2 px-2 text-right">{r.android}</td>
                  <td className="py-2 px-2 text-right">{r.ios}</td>
                  <td className="py-2 px-2 text-right">{r.web}</td>
                  <td className="py-2 px-2 text-right font-semibold text-emerald-600">{r.signups}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
